'use client';

import { memo, useCallback } from 'react';
import { Box, Typography, IconButton, Stack, Chip, useTheme, alpha } from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalShipping as ShippingIcon,
  Inventory2 as BoxIcon,
} from '@mui/icons-material';
import { COLORS } from '../utils/constants';
import { useTranslation } from '@/kit/useTranslation';

// Preset unit options for quick selection
const PRESET_UNITS = [1, 5, 10, 25, 50];

/**
 * UnitsSelector - Allows business users to select number of parcels/boxes for bulk booking
 * Shows a counter with increment/decrement and preset quick-select chips
 */
const UnitsSelector = memo(
  ({ value = 1, onChange, minUnits = 1, maxUnits = 999, isDesktop = false }) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const handleIncrement = useCallback(() => {
      if (value < maxUnits) {
        onChange(value + 1);
      }
    }, [value, maxUnits, onChange]);

    const handleDecrement = useCallback(() => {
      if (value > minUnits) {
        onChange(value - 1);
      }
    }, [value, minUnits, onChange]);

    const handlePresetSelect = useCallback(
      (preset) => {
        onChange(Math.min(Math.max(preset, minUnits), maxUnits));
      },
      [minUnits, maxUnits, onChange]
    );

    return (
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          background: '#fff',
          borderRadius: 3,
          border: '1px solid rgba(226, 232, 240, 0.8)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
          mb: 2,
        }}
      >
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BoxIcon sx={{ color: '#fff', fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1rem', md: '1.1rem' },
                color: '#0f172a',
              }}
            >
              {t('estimate.units.title')}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
              {t('estimate.units.subtitle')}
            </Typography>
          </Box>
        </Stack>

        {/* Counter */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: { xs: 2, md: 3 },
            mb: 2.5,
          }}
        >
          <IconButton
            onClick={handleDecrement}
            disabled={value <= minUnits}
            sx={{
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              bgcolor: value <= minUnits ? '#f1f5f9' : alpha(COLORS.primary, 0.08),
              color: value <= minUnits ? '#94a3b8' : COLORS.primary,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(COLORS.primary, 0.15),
                transform: 'scale(1.05)',
              },
              '&:disabled': {
                bgcolor: '#f1f5f9',
              },
            }}
          >
            <RemoveIcon fontSize={isDesktop ? 'medium' : 'small'} />
          </IconButton>

          <Box
            sx={{
              minWidth: { xs: 100, md: 140 },
              textAlign: 'center',
              py: 2,
              px: 3,
              bgcolor: alpha(COLORS.primary, 0.05),
              borderRadius: 3,
              border: `2px solid ${alpha(COLORS.primary, 0.2)}`,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.5rem' },
                color: COLORS.primary,
                lineHeight: 1,
              }}
            >
              {value}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>
              {value === 1 ? t('estimate.units.box') : t('estimate.units.boxes')}
            </Typography>
          </Box>

          <IconButton
            onClick={handleIncrement}
            disabled={value >= maxUnits}
            sx={{
              width: { xs: 48, md: 56 },
              height: { xs: 48, md: 56 },
              bgcolor: value >= maxUnits ? '#f1f5f9' : alpha(COLORS.primary, 0.08),
              color: value >= maxUnits ? '#94a3b8' : COLORS.primary,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: alpha(COLORS.primary, 0.15),
                transform: 'scale(1.05)',
              },
              '&:disabled': {
                bgcolor: '#f1f5f9',
              },
            }}
          >
            <AddIcon fontSize={isDesktop ? 'medium' : 'small'} />
          </IconButton>
        </Box>

        {/* Preset Quick Select Chips */}
        <Box>
          <Typography
            variant="caption"
            sx={{ color: '#94a3b8', fontWeight: 600, mb: 1, display: 'block' }}
          >
            {t('estimate.units.quickSelect')}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
            {PRESET_UNITS.map((preset) => (
              <Chip
                key={preset}
                label={`${preset} ${preset === 1 ? t('estimate.units.box') : t('estimate.units.boxes')}`}
                onClick={() => handlePresetSelect(preset)}
                variant={value === preset ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  bgcolor:
                    value === preset
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : 'transparent',
                  background:
                    value === preset
                      ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                      : undefined,
                  color: value === preset ? '#fff' : '#64748b',
                  borderColor: value === preset ? 'transparent' : '#e2e8f0',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: value === preset ? alpha('#667eea', 0.9) : alpha('#667eea', 0.08),
                    borderColor: '#667eea',
                    transform: 'translateY(-2px)',
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Info text for bulk savings */}
        {value > 1 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: alpha('#10b981', 0.08),
              borderRadius: 2,
              border: `1px solid ${alpha('#10b981', 0.2)}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ShippingIcon sx={{ color: '#10b981', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600 }}>
                {t('estimate.units.bulkOrder', { count: value })}
              </Typography>
            </Stack>
          </Box>
        )}
      </Box>
    );
  }
);

UnitsSelector.displayName = 'UnitsSelector';

export default UnitsSelector;
